import { useEffect, useRef, useState } from 'react';

/**
 * ReactBits — AnimatedContent.
 *
 * Lightweight scroll-reveal wrapper. Fades + slides children in when they
 * enter the viewport. Pure CSS transitions; respects prefers-reduced-motion.
 *
 * Props:
 *   distance  — pixels of slide (default 24)
 *   direction — "vertical" | "horizontal" (default vertical)
 *   reverse   — invert slide direction
 *   delay     — ms before the transition kicks in (default 0)
 *   duration  — ms transition length (default 700)
 *   threshold — IntersectionObserver threshold (default 0.15)
 *   initialOpacity — opacity before reveal (default 0)
 */
const AnimatedContent = ({
  children,
  distance = 24,
  direction = 'vertical',
  reverse = false,
  delay = 0,
  duration = 700,
  threshold = 0.15,
  initialOpacity = 0,
  className = '',
  as: Tag = 'div',
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px -8% 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  const sign = reverse ? -1 : 1;
  const tx = direction === 'horizontal' ? sign * distance : 0;
  const ty = direction === 'vertical' ? sign * distance : 0;

  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : initialOpacity,
        transform: visible
          ? 'translate3d(0,0,0)'
          : `translate3d(${tx}px, ${ty}px, 0)`,
        transition: `opacity ${duration}ms ease-out ${delay}ms, transform ${duration}ms ease-out ${delay}ms`,
        willChange: 'opacity, transform',
      }}
    >
      {children}
    </Tag>
  );
};

export default AnimatedContent;
